<template>
  <div class="scroll-area" ref="wrapperEl">
    <!-- Shadows -->
    <div :class="['scroll-area__shadow', 'scroll-area__shadow--top', { visible: topShadow }]" />
    <div :class="['scroll-area__shadow', 'scroll-area__shadow--bottom', { visible: bottomShadow }]" />

    <!-- Scroll -->
    <VueScroll :ops="settings" class="scroll-area__scroll" ref="scrollEl" @handle-scroll="handleScroll">
      <div ref="contentEl">
        <slot />
      </div>
    </VueScroll>
  </div>
</template>

<style lang="postcss" scoped>
.scroll-area {
  @apply relative;

  &__scroll {
    @apply w-full h-full overflow-auto;

    .__panel {
      @apply max-w-full;
    }

    /deep/ .__view {
      @apply max-w-full;
    }
  }

  &__shadow {
    @apply absolute w-full opacity-0 pointer-events-none h-xxxs z-[1];
    @apply transition-[opacity_100ms_ease-in-out];

    &:after {
      @apply absolute h-full left-0 right-0 shadow-lg content-[''];
    }

    &.visible {
      @apply opacity-100;
    }

    &--top {
      @apply top-0;

      &:after {
        @apply bottom-full;
      }
    }

    &--bottom {
      @apply bottom-0;

      &:after {
        @apply top-full rotate-180;
      }
    }
  }
}
</style>

<script>
import { computed, onMounted, ref, toRefs, watch } from '@vue/composition-api';
import { onElementResize } from '@/lego/utils/dom/element';
import VueScroll from 'vuescroll';

export default {
  name: 'ScrollAreaComponent',

  components: {
    VueScroll,
  },

  props: {
    disable: {
      type: Boolean,
      default: false,
    },

    shadow: {
      type: Boolean,
      default: false,
    },

    // https://vuescrolljs.yvescoding.me/guide/configuration.html#scrollpanel
    scrollOptions: {
      type: Object,
      default: () => {},
    },

    // https://vuescrolljs.yvescoding.me/guide/configuration.html#rail
    railOptions: {
      type: Object,
      default: () => {},
    },

    // https://vuescrolljs.yvescoding.me/guide/configuration.html#bar
    barOptions: {
      type: Object,
      default: () => {},
    },

    // https://vuescrolljs.yvescoding.me/guide/configuration.html#scrollbutton
    buttonOptions: {
      type: Object,
      default: () => {},
    },
  },

  setup(props, { emit }) {
    const { disable, shadow, scrollOptions, railOptions, barOptions, buttonOptions } = toRefs(props);

    const wrapperEl = ref();
    const contentEl = ref();
    const scrollEl = ref();

    const wrapperHeight = ref(0);
    const contentHeight = ref(0);

    const hasScroll = computed(() => contentHeight.value > wrapperHeight.value);

    const scrollingDown = ref(false);
    const scrollingUp = ref(false);
    const scrollStartReached = ref(true);
    const scrollEndReached = ref(false);

    const topShadow = computed(() => shadow.value && hasScroll.value && !scrollStartReached.value);
    const bottomShadow = computed(() => shadow.value && hasScroll.value && !scrollEndReached.value);

    // https://vuescrolljs.yvescoding.me/guide/configuration.html
    const settings = computed(() => ({
      vuescroll: {
        mode: 'native',
        sizeStrategy: 'percent',
        detectResize: true,
        locking: true, // https://github.com/YvesCoding/vuescroll/issues/197
      },
      scrollPanel: {
        speed: 300,
        easing: 'easeInQuad', // https://vuescrolljs.yvescoding.me/guide/configuration.html#easing
        verticalNativeBarPos: 'right',
        ...scrollOptions.value,
      },
      rail: {
        background: '#01a99a',
        opacity: 0,
        size: '6px',
        specifyBorderRadius: false,
        gutterOfEnds: null,
        gutterOfSide: '2px',
        keepShow: false,
        ...railOptions.value,
      },
      bar: {
        showDelay: 500,
        onlyShowBarOnScroll: false,
        keepShow: false,
        background: '#c1c1c1',
        opacity: 1,
        hoverStyle: false,
        specifyBorderRadius: false,
        minSize: 0,
        size: '6px',
        disable: disable.value,
        ...barOptions.value,
      },
      scrollButton: {
        enable: false,
        background: 'rgb(3, 185, 118)',
        opacity: 1,
        step: 180,
        mousedownStep: 30,
        ...buttonOptions.value,
      },
    }));

    function useScrollEvents() {
      let lastScrollTop = 0;

      function handleScrollDown() {
        scrollingDown.value = true;
        scrollingUp.value = false;
        scrollStartReached.value = false;
      }

      function handleScrollUp() {
        scrollingUp.value = true;
        scrollingDown.value = false;
        scrollEndReached.value = false;
      }

      function handleScrollReachStart() {
        scrollStartReached.value = true;
      }

      function handleScrollReachEnd() {
        scrollEndReached.value = true;
      }

      function handleScroll(evt) {
        if (evt.process === 0) {
          handleScrollReachStart();
        }

        if (evt.process === 1) {
          handleScrollReachEnd();
        }

        if (evt.scrollTop > lastScrollTop) {
          handleScrollDown();
        }

        if (evt.scrollTop < lastScrollTop) {
          handleScrollUp();
        }

        lastScrollTop = evt.scrollTop;

        emit('scroll', evt);
      }

      return { handleScroll };
    }

    onMounted(() => {
      onElementResize(wrapperEl.value, ({ height }) => (wrapperHeight.value = height));
      onElementResize(contentEl.value, ({ height }) => (contentHeight.value = height));
    });

    watch(wrapperHeight, () => {
      scrollStartReached.value = true;
      scrollEndReached.value = false;
    });

    return {
      wrapperEl,
      contentEl,
      scrollEl,
      settings,
      topShadow,
      bottomShadow,
      ...useScrollEvents(),
    };
  },
};
</script>
